import { useState } from 'react';
import { PageWrapper } from '@/components/layout/PageWrapper';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Shield, Users, Crown, Briefcase, Search, UserPlus } from 'lucide-react';
import { ROLE_LABELS, AppRole } from '@/hooks/useUserRoles';
import { RoleProtectedRoute } from '@/components/auth/RoleProtectedRoute';

export default function RoleManagement() {
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch all employees with their user_ids
  const { data: employees, isLoading } = useQuery({
    queryKey: ['employees-with-users'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('employees')
        .select('id, name, email, user_id, position, status')
        .order('name');
      
      if (error) throw error;
      return data;
    }
  });

  const filteredEmployees = employees?.filter(emp => 
    emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const roleColors: Record<AppRole, string> = {
    'super_admin': '#dc2626',
    'admin': '#ea580c',
    'manager': '#d97706',
    'sales_manager': '#16a34a',
    'sales': '#22c55e',
    'hr_manager': '#0284c7',
    'hr': '#0ea5e9',
    'accountant': '#7c3aed',
    'receptionist': '#db2777',
    'user': '#64748b'
  };

  return (
    <RoleProtectedRoute requiredRoles={['super_admin', 'admin']}>
      <PageWrapper>
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-2">
                <Shield className="w-8 h-8 text-primary" />
                إدارة أدوار النظام (RBAC)
              </h1>
              <p className="text-muted-foreground mt-2">
                إدارة وتعيين أدوار المستخدمين في نظام التحكم بالوصول
              </p>
            </div>
            <Button>
              <UserPlus className="w-4 h-4 ml-2" />
              تعيين دور جديد
            </Button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Crown className="w-4 h-4 text-red-600" />
                  المدراء
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">0</div>
                <p className="text-xs text-muted-foreground">Super Admin & Admin</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-orange-600" />
                  مدراء الأقسام
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">0</div>
                <p className="text-xs text-muted-foreground">Managers</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-600" />
                  الموظفين
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{employees?.length || 0}</div>
                <p className="text-xs text-muted-foreground">إجمالي الموظفين</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Shield className="w-4 h-4 text-green-600" />
                  بدون أدوار
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {employees?.filter(e => !e.user_id).length || 0}
                </div>
                <p className="text-xs text-muted-foreground">يحتاجون تعيين أدوار</p>
              </CardContent>
            </Card>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute right-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="ابحث عن موظف..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pr-10"
            />
          </div>

          {/* Notice */}
          <Card className="border-amber-200 bg-amber-50">
            <CardHeader>
              <CardTitle className="text-amber-800 text-sm flex items-center gap-2">
                <Shield className="w-4 h-4" />
                ملاحظة هامة
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-amber-700">
              <p>
                نظام RBAC يتطلب تطبيق Database Migration أولاً. 
                بعد تطبيق Migration، ستتمكن من تعيين الأدوار مباشرةً من صفحة تعديل الموظف.
              </p>
              <p className="mt-2 font-medium">
                لتطبيق Migration: انتقل إلى قسم Database في Supabase Dashboard
              </p>
            </CardContent>
          </Card>

          {/* Employees List */}
          <Card>
            <CardHeader>
              <CardTitle>الموظفين المسجلين</CardTitle>
              <CardDescription>
                قائمة بجميع الموظفين في النظام وحالة أدوارهم
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="text-center py-8">جاري التحميل...</div>
              ) : (
                <div className="space-y-2">
                  {filteredEmployees?.map((employee) => (
                    <div
                      key={employee.id}
                      className="flex items-center justify-between p-4 rounded-lg border hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex-1">
                        <h3 className="font-medium">{employee.name}</h3>
                        <p className="text-sm text-muted-foreground">{employee.email}</p>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <Badge variant="outline">{employee.position}</Badge>
                        
                        {employee.user_id ? (
                          <Badge 
                            variant="secondary"
                            className="gap-1"
                          >
                            <Shield className="w-3 h-3" />
                            لديه حساب
                          </Badge>
                        ) : (
                          <Badge variant="destructive" className="gap-1">
                            لا يوجد حساب
                          </Badge>
                        )}
                        
                        <Button size="sm" variant="outline">
                          إدارة الأدوار
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </PageWrapper>
    </RoleProtectedRoute>
  );
}
